import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { ICS204_TABLE } from '../../AssignmentList/assignmentCardStyles';

const DIALOG_SURFACE =
  'dark tw:border-[var(--border-primary)] tw:bg-black tw:text-foreground';

export type EditEquipmentTypesDrawerProps = {
  toggle: () => void;
  equipmentTypes: string[];
  onSave: (equipmentTypes: Set<string>) => void;
};

/**
 * The equipment columns of box 16.
 *
 * The columns are shared across every agency, so they are edited here as one
 * list rather than per row. Saving with a blank name would leave a column
 * nothing could be entered against, so Save waits until all are filled.
 */
const AssignedResourcesEquipmentTypesDrawer: React.FC<
  EditEquipmentTypesDrawerProps
> = ({ toggle, equipmentTypes, onSave }) => {
  const [types, setTypes] = useState<string[]>(equipmentTypes ?? []);

  const saveDisabled = types.some((type) => !type.trim());

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) toggle();
      }}
    >
      <DialogContent
        className={DIALOG_SURFACE}
        showCloseButton={false}
        data-testid="ics209-equipment-types-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit Equipment Types</DialogTitle>
        </DialogHeader>

        <Table className={ICS204_TABLE}>
          <TableHeader>
            <TableRow>
              <TableHead>Equipment Type</TableHead>
              <TableHead className="tw:w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="tw:text-center">
                  There are no equipment types yet. Click Add Equipment Type to
                  add one.
                </TableCell>
              </TableRow>
            ) : (
              types.map((type, index) => (
                // Rows are identified by position: the names are still being
                // typed, so they are not yet unique enough to key on.
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      aria-label={`Equipment type ${index + 1}`}
                      value={type}
                      onChange={(event) =>
                        setTypes((current) =>
                          current.map((existing, i) =>
                            i === index ? event.target.value : existing
                          )
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete equipment type ${index + 1}`}
                      onClick={() =>
                        setTypes((current) =>
                          current.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <Trash2 className="tw:size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div>
          <Button
            type="button"
            size="sm"
            onClick={() => setTypes((current) => [...current, ''])}
          >
            <Plus className="tw:size-4" />
            Add Equipment Type
          </Button>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={toggle}>
            Close
          </Button>
          <Button
            type="button"
            disabled={saveDisabled}
            onClick={() => {
              onSave(new Set(types));
              toggle();
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignedResourcesEquipmentTypesDrawer;
